import React, { useEffect, useRef } from 'react';
import {
    CategoryContainer,
    CategoryIcon,
    CategoryLabel,
    CategoryOption,
    DropdownArrow,
    DropdownItemsContainer,
    DropdownWrapper,
    ExternalLink,
    InternalLink,
    PageLinkContainer,
} from '../../styles/dropdown.style';
import PropTypes from 'prop-types';
import PageLink from '../PageLink';
import gsap from 'gsap';
import useClickOutside from '../../../../hooks/useClickOutside';

const Dropdown = ({
    position,
    onCloseDropdown,
    navItemsRef,
    onMouseEnter,
    onMouseLeave,
    activeDropdownCategory,
    setActiveDropdownCategory,
    menuItems,
    dropdownCategories,
}) => {
    const dropdownContainerRef = useRef(null);
    const linksContainerRef = useRef(null);
    const arrowRef = useRef(null);

    useClickOutside({
        handler: onCloseDropdown,
        refs: [dropdownContainerRef, navItemsRef],
    });

    // Animate category links on change
    useEffect(() => {
        const container = linksContainerRef.current;
        if (container) {
            gsap.fromTo(
                container,
                { opacity: 0, x: -20 },
                { opacity: 1, x: 0, duration: 0.3, ease: 'power1.in' }
            );
        }
    }, [activeDropdownCategory]);

    // Animate arrow position when navtag changes
    useEffect(() => {
        if (arrowRef.current && position != null) {
            gsap.to(arrowRef.current, {
                left: `${position}px`,
                duration: 0.2,
                ease: 'none',
            });
        }
    }, [position]);

    return (
        <DropdownWrapper>
            <DropdownItemsContainer
                ref={dropdownContainerRef}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <DropdownArrow ref={arrowRef} />
                <CategoryContainer>
                    {dropdownCategories &&
                        Object.entries(dropdownCategories)?.map(([key, category]) => (
                            <CategoryOption
                                key={category.label}
                                onMouseEnter={() => setActiveDropdownCategory(category.label)}
                                onClick={() => setActiveDropdownCategory(category.label)}
                                active={activeDropdownCategory === category.label}
                            >
                                <CategoryIcon src={category.icon} alt={`${category.label}-icon`} />
                                <CategoryLabel>{category.label}</CategoryLabel>
                            </CategoryOption>
                        ))}
                </CategoryContainer>

                <PageLinkContainer ref={linksContainerRef}>
                    {menuItems?.[activeDropdownCategory]?.map((item) =>
                        item?.external ? (
                            <ExternalLink
                                href={item.link}
                                key={`${item.label}-${item.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <PageLink item={item} />
                            </ExternalLink>
                        ) : (
                            <InternalLink to={item.link} key={`${item.label}-${item.id}`}>
                                <PageLink item={item} />
                            </InternalLink>
                        )
                    )}
                </PageLinkContainer>
            </DropdownItemsContainer>
        </DropdownWrapper>
    );
};

Dropdown.propTypes = {
    position: PropTypes.number,
    navItemsRef: PropTypes.element,
    onCloseDropdown: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    activeDropdownCategory: PropTypes.string,
    setActiveDropdownCategory: PropTypes.func,
    menuItems: PropTypes.array,
    dropdownCategories: PropTypes.string,
};

export default Dropdown;
